import { FiGlobe, FiInbox, FiCpu, FiBriefcase, FiHeart, FiBookOpen, FiSettings } from "react-icons/fi";
import { ENTITY, PALETTE } from "./dashboard/chartUtils";

// The menu of the admin panel, in separate sections. Every section has its own colour (the same colour its charts have on the dashboard).
// `badge` names a counter of the dashboard ("what is waiting"); a section shows the total of its links' counters.

// Pages with their own sub-menu inside the Website section, keyed by the page's menu slug.
export const PAGE_GROUPS = {
  home: [
    { label: "Browser Title", to: "/admin/pages/home" },
    { label: "Home Banner", to: "/admin/pages/home/hero-carousel" },
    { label: "Services", to: "/admin/pages/home/services" },
    { label: "Sub Services", to: "/admin/pages/home/sub-services" },
    { label: "Systems", to: "/admin/pages/home/systems" },
  ],
  portfolio: [
    { label: "Banner & Body", to: "/admin/pages/portfolio" },
    { label: "Projects", to: "/admin/pages/portfolio/projects" },
  ],
};

// Pages of the menu that have a section of their own, so they are not listed again under Website.
export const SEPARATE_PAGES = ["co-work", "foundation", "blogs", "ai-tools", "contact"];

export const NAV_SECTIONS = [
  { id: "website", label: "Website", icon: FiGlobe, color: PALETTE[0], pages: true, links: [] },
  {
    id: "inbox", label: "Enquiries", icon: FiInbox, color: ENTITY.inbox, links: [
      { label: "Contact Messages", to: "/admin/pages/contact/messages", badge: "contact_unread" },
      { label: "Quote Requests", to: "/admin/pages/contact/quotes" },
      { label: "Contact Page Content", to: "/admin/pages/contact" },
    ],
  },
  {
    id: "ai", label: "AI Tools & Chatbots", icon: FiCpu, color: ENTITY.chat, links: [
      { label: "AI Tool Categories", to: "/admin/pages/ai-tools/categories" },
      { label: "AI Chatbots", to: "/admin/pages/ai-tools/chatbots", badge: "chatbots" },
    ],
  },
  {
    id: "cowork", label: "Co-Work", icon: FiBriefcase, color: ENTITY.cowork, links: [
      { label: "Browser Title & Logo", to: "/admin/pages/co-work" },
      { label: "Banner Slides", to: "/admin/pages/co-work/slides" },
      { label: "Marketplace Content", to: "/admin/pages/co-work/content" },
      { label: "Jobs", to: "/admin/pages/co-work/jobs", badge: "cowork_open_jobs", quiet: true },
      { label: "Members", to: "/admin/pages/co-work/members" },
    ],
  },
  {
    id: "foundation", label: "Foundation", icon: FiHeart, color: ENTITY.foundation, links: [
      { label: "Banner & Body", to: "/admin/pages/foundation" },
      { label: "Help Requests", to: "/admin/pages/foundation/requests", badge: "foundation_requests" },
      { label: "Help Offers", to: "/admin/pages/foundation/offers", badge: "foundation_offers" },
      { label: "Foundation Content", to: "/admin/pages/foundation/content" },
    ],
  },
  {
    id: "blogs", label: "Blogs", icon: FiBookOpen, color: ENTITY.blogs, links: [
      { label: "Blog Posts", to: "/admin/pages/blogs/posts", badge: "blog_drafts", quiet: true },
      { label: "Blogs Banner & Body", to: "/admin/pages/blogs" },
      { label: "IT Services Blogs", to: "/admin/pages/blogs-it-services" },
      { label: "AI Tools Blogs", to: "/admin/pages/blogs-ai-tools" },
      { label: "CoWork Marketplace Blogs", to: "/admin/pages/blogs-cowork-marketplace" },
      { label: "Foundation Blogs", to: "/admin/pages/blogs-foundation" },
    ],
  },
  {
    id: "settings", label: "Site Settings", icon: FiSettings, color: "#898781", links: [
      { label: "Site Identity", to: "/admin/settings/site-identity" },
      { label: "Navbar", to: "/admin/settings/navbar" },
      { label: "Footer", to: "/admin/settings/footer" },
    ],
  },
];

// The number to show next to a link. "chatbots" adds up everything the chatbots leave for a person.
export function badgeValue(badges, key) {
  if (!badges || !key) return 0;
  if (key === "chatbots") return (badges.tickets_open || 0) + (badges.leads_new || 0) + (badges.chats_waiting || 0);
  return badges[key] || 0;
}

// What needs a person in a section: links marked `quiet` (counts that are information, not work) are left out of the section total.
export function sectionBadge(section, badges) {
  return section.links.reduce((total, l) => total + (l.quiet ? 0 : badgeValue(badges, l.badge)), 0);
}

// Which section owns an address (so it can open by itself).
export function sectionOfPath(pathname) {
  const p = String(pathname || "");
  if (p.startsWith("/admin/settings")) return "settings";
  if (p.startsWith("/admin/pages/ai-tools")) return "ai";
  if (p.startsWith("/admin/pages/co-work")) return "cowork";
  if (p.startsWith("/admin/pages/foundation")) return "foundation";
  if (p.startsWith("/admin/pages/blogs")) return "blogs";
  if (p.startsWith("/admin/pages/contact")) return "inbox";
  if (p.startsWith("/admin/pages")) return "website";
  return null;
}

// The pages of the menu that belong under Website, in order.
export function websitePages(menu) {
  return (menu || []).filter((m) => !m.parent && !SEPARATE_PAGES.includes(m.slug)).sort((a, b) => a.order - b.order);
}

// Every link of the menu as a flat list, for the search box: { section, label, to, group }
export function flatLinks(menu) {
  const out = [];
  NAV_SECTIONS.forEach((section) => {
    if (section.pages) {
      websitePages(menu).forEach((page) => {
        const group = PAGE_GROUPS[page.slug];
        if (group) group.forEach((l) => out.push({ section: section.id, label: l.label, to: l.to, group: page.title }));
        else out.push({ section: section.id, label: page.title, to: `/admin/pages/${page.slug}`, group: "" });
      });
    }
    section.links.forEach((l) => out.push({ section: section.id, label: l.label, to: l.to, group: "", badge: l.badge }));
  });
  return out;
}

export function searchLinks(menu, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  return flatLinks(menu).filter((l) => `${l.group} ${l.label}`.toLowerCase().includes(q));
}
